const FULL_ALERT_SYSTEM_SCHEMA = {
  alerts: { 
    keyPath: 'id', 
    indexes: [ 
      { name: 'name_idx', keyPath: 'name'},
      { name: 'type_idx', keyPath: 'type'},
      { name: 'severity_idx', keyPath: 'severity'},
      { name: 'scope_type_idx', keyPath: 'scope_type'},
      { name: 'user_id_idx', keyPath: 'user_id'},
      { name: 'enabled_idx', keyPath: 'enabled'},
      { name: 'updated_at_idx', keyPath: 'updated_at'}
    ] 
  },
  alert_rules: { 
    keyPath: 'id', 
    indexes: [ 
      { name: 'alert_id_idx', keyPath: 'alert_id'},
      { name: 'event_idx', keyPath: 'event'}
    ] 
  },
  alert_server_scopes: {
    keyPath: 'id', 
    indexes: [ 
      { name: 'alert_id_idx', keyPath: 'alert_id'},
      { name: 'server_id_idx', keyPath: 'server_id'}
    ] 
  },
  alert_actions: { 
    keyPath: 'id', 
    indexes: [ 
      { name: 'alert_id_idx', keyPath: 'alert_id'},
      { name: 'action_type_idx', keyPath: 'action_type'}
    ] 
  },
  notifications: { 
    keyPath: 'id', 
    indexes: [ 
      { name: 'alert_id_idx', keyPath: 'alert_id'},
      { name: 'severity_idx', keyPath: 'severity'},
      { name: 'timestamp_idx', keyPath: 'timestamp'},
      { name: 'read_idx', keyPath: 'read'},
      { name: 'user_id_idx', keyPath: 'user_id'},
      { name: 'user_read_idx', keyPath: ['user_id', 'read']}
    ] 
  }
};

/**
 * DatabaseService
 * 
 * Provides a high-level interface for managing IndexedDB connections and transactions.
 * Supports CRUD operations for any object store, with robust error handling and promise-based API.
 * Used as the primary persistence layer for the alert system.
 * 
 * Usage:
 *   const db = new DatabaseService('DeepRDashboardDB', 1, { alerts: { keyPath: 'id' }, notifications: { keyPath: 'id' } });
 *   await db.connect();
 *   await db.add('alerts', { id: '1', name: 'Test Alert' });
 *
 * The default database name is 'DeepRDashboardDB', making it suitable for all features and modules in DeepRDashboard, not just alerts.
 */
class DatabaseService {
  /**
   * @param {string} dbName - The name of the IndexedDB database
   * @param {number} version - The version number for schema upgrades
   * @param {Object} stores - Object store definitions, e.g. { alerts: { keyPath: 'id' } }
   */
  constructor(dbName = 'DeepRDashboardDB', version = 2, stores = FULL_ALERT_SYSTEM_SCHEMA) {
    this.dbName = dbName;
    this.version = version;
    this.stores = stores;
    this.db = null;
    this.connectPromise = null; // Initialize connectPromise
  }

  /**
   * Deletes the database completely
   * Use this when there's a schema mismatch that requires a complete rebuild
   * @returns {Promise<void>}
   */
  async deleteDatabase() {
    // Close any existing connection
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    
    this.connectPromise = null;
    
    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(this.dbName);
      
      deleteRequest.onsuccess = () => {
        console.log(`Database '${this.dbName}' successfully deleted`);
        resolve();
      };
      
      deleteRequest.onerror = (event) => {
        console.error(`Error deleting database '${this.dbName}':`, event.target.error);
        reject(event.target.error);
      };
      
      deleteRequest.onblocked = () => {
        console.warn(`Database deletion blocked for '${this.dbName}'. Some connection may still be open.`);
        // We still resolve, as we'll try to open with the new schema
        resolve();
      };
    });
  }
  
  /**
   * Verifies that the database schema matches the expected schema
   * @returns {Promise<boolean>} - True if schema is valid, false if mismatch
   */
  async verifySchema() {
    try {
      // Temporarily connect to the database
      const tempRequest = indexedDB.open(this.dbName);
      
      return new Promise((resolve) => {
        tempRequest.onsuccess = (event) => {
          const db = event.target.result;
          const storeNames = Array.from(db.objectStoreNames);
          const expectedStoreNames = Object.keys(this.stores);
          
          // Check if all expected stores exist
          const missingStores = expectedStoreNames.filter(name => !storeNames.includes(name));
          
          // Close this temporary connection
          db.close();
          
          if (missingStores.length > 0) {
            console.warn(`Schema verification failed. Missing stores: ${missingStores.join(', ')}`);
            resolve(false);
          } else {
            console.log('Schema verification successful');
            resolve(true);
          }
        };
        
        tempRequest.onerror = () => {
          console.error('Error during schema verification');
          resolve(false); // Assume schema is invalid if we can't verify
        };
      });
    } catch (error) {
      console.error('Exception during schema verification:', error);
      return false;
    }
  }
  
  /**
   * Opens a connection to IndexedDB, creating object stores if needed.
   * Returns a promise that resolves with the IDBDatabase instance.
   * @param {boolean} forceReset - If true, delete and recreate the database
   * @returns {Promise<IDBDatabase>}
   */
  async connect(forceReset = false) {
    // Check if we need to force a reset of the database
    if (forceReset) {
      try {
        await this.deleteDatabase();
        console.log('Database reset completed, proceeding with fresh connection');
      } catch (error) {
        console.error('Error during forced database reset:', error);
        // Continue anyway, as the open request below may still succeed
      }
    } else {
      // If not forcing reset, verify schema and reset if needed
      const schemaValid = await this.verifySchema();
      if (!schemaValid) {
        console.warn('Schema invalid, forcing database reset...');
        try {
          await this.deleteDatabase();
          console.log('Database reset completed after schema verification');
        } catch (error) {
          console.error('Error during schema-triggered database reset:', error);
        }
      }
    }
    
    // 1. If already connected, return a resolved promise with the db instance
    if (this.db) {
      return Promise.resolve(this.db);
    }

    // 2. If a connection attempt is already in progress, return that promise
    if (this.connectPromise) {
      return this.connectPromise;
    }

    // 3. Otherwise, start a new connection attempt
    this.connectPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = (event) => {
        try {
          const db = event.target.result;
          const transaction = event.target.transaction;

          try {
            Object.entries(this.stores).forEach(([storeName, storeConfig]) => {
              let objectStore;
              if (!db.objectStoreNames.contains(storeName)) {
                const storeOptions = { keyPath: storeConfig.keyPath };
                if (typeof storeConfig.autoIncrement === 'boolean') {
                  storeOptions.autoIncrement = storeConfig.autoIncrement;
                }
                objectStore = db.createObjectStore(storeName, storeOptions);
              } else {
                if (!transaction) {
                  console.error(`Transaction not available for upgrading existing store '${storeName}'. This should not happen during onupgradeneeded.`);
                  return;
                }
                objectStore = transaction.objectStore(storeName);
              }

              if (storeConfig.indexes && objectStore) {
                storeConfig.indexes.forEach(indexDef => {
                  if (!objectStore.indexNames.contains(indexDef.name)) {
                    try {
                      objectStore.createIndex(indexDef.name, indexDef.keyPath, indexDef.options || {});
                    } catch (e) {
                      console.error(`Failed to create index '${indexDef.name}' on store '${storeName}':`, e);
                    }
                  }
                });
              }
            });
          } catch (e) {
            console.error('onupgradeneeded error:', e);
            throw e;
          }
        } catch (e) {
          console.error('onupgradeneeded error:', e);
          throw e;
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        // Don't nullify connectPromise here; it holds the successfully resolved promise.
        resolve(this.db); // 5. Resolve with the database instance
      };

      request.onerror = (event) => {
        this.connectPromise = null; // 6. Reset connectPromise on error
        reject(new Error('IndexedDB connection failed: ' + event.target.errorCode));
      };
    });

    return this.connectPromise;
  }

  /**
   * Closes the database connection.
   */
  disconnect() {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.connectPromise = null;
    }
  }

  /**
   * Executes a transaction and returns the object store.
   * @param {string} storeName
   * @param {string} mode - 'readonly' or 'readwrite'
   * @returns {Promise<IDBObjectStore>}
   */
  async getStore(storeName, mode = 'readwrite') {
    // Ensure database is connected before proceeding
    if (!this.db) {
      try {
        console.log('Database not connected, attempting to connect...');
        await this.connect();
      } catch (error) {
        console.error('Failed to connect to database:', error);
        throw new Error('Failed to connect to database: ' + error.message);
      }
    }
    
    if (!this.db) {
      throw new Error('Database not connected after connection attempt');
    }
    
    const tx = this.db.transaction([storeName], mode);
    return tx.objectStore(storeName);
  }

  /**
   * Adds a new record to the store.
   * @param {string} storeName
   * @param {Object} value
   * @returns {Promise<IDBValidKey>}
   */
  add(storeName, value) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName);
        const request = store.add(value);
        request.onsuccess = (e) => resolve(e.target.result);
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('Add operation failed:', err);
        reject(err);
      }
    });
  }

  /**
   * Updates a record in the store.
   * @param {string} storeName
   * @param {Object} value
   * @returns {Promise<IDBValidKey>}
   */
  put(storeName, value) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName);
        const request = store.put(value);
        request.onsuccess = (e) => resolve(e.target.result);
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('Put operation failed:', err);
        reject(err);
      }
    });
  }

  /**
   * Retrieves a record by key.
   * @param {string} storeName
   * @param {IDBValidKey} key
   * @returns {Promise<Object|null>}
   */
  get(storeName, key) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName, 'readonly');
        const request = store.get(key);
        request.onsuccess = (e) => resolve(e.target.result || null);
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('Get operation failed:', err);
        reject(err);
      }
    });
  }

  /**
   * Deletes a record by key.
   * @param {string} storeName
   * @param {IDBValidKey} key
   * @returns {Promise<void>}
   */
  delete(storeName, key) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('Delete operation failed:', err);
        reject(err);
      }
    });
  }

  /**
   * Retrieves all records from the store.
   * @param {string} storeName
   * @returns {Promise<Object[]>}
   */
  getAll(storeName) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName, 'readonly');
        const request = store.getAll();
        request.onsuccess = (e) => resolve(e.target.result || []);
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('GetAll operation failed:', err);
        reject(err);
      }
    });
  }
  
  /**
   * Clears all records from a store
   * @param {string} storeName - The store to clear
   * @returns {Promise<void>}
   */
  clear(storeName) {
    return new Promise(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = (e) => reject(e.target.error);
      } catch (err) {
        console.error('Clear operation failed:', err);
        reject(err);
      }
    });
  }
}

/**
 * Begin a transaction for one or more object stores.
 * @param {string[]} storeNames - Array of object store names to include in the transaction
 * @param {string} mode - Transaction mode ('readonly' or 'readwrite')
 * @returns {Promise<IDBTransaction>} - Promise resolving to the transaction object
 */
DatabaseService.prototype.beginTransaction = async function(storeNames, mode = 'readwrite') {
  try {
    // Ensure database is connected
    if (!this.db) {
      console.log('Database not connected, attempting to connect before transaction...');
      await this.connect();
    }
    
    if (!this.db) {
      throw new Error('Database not connected after connection attempt');
    }
    
    // Create a transaction spanning all the specified stores
    const transaction = this.db.transaction(storeNames, mode);
    
    // Log transaction creation
    console.debug(`Transaction created for stores: [${storeNames.join(', ')}] with mode: ${mode}`);
    
    return transaction;
  } catch (error) {
    console.error('Failed to begin transaction:', error);
    throw error;
  }
};

/**
 * Execute operations within a transaction and handle commit/abort automatically.
 * @param {string[]} storeNames - Array of object store names to include in the transaction
 * @param {function(IDBTransaction): Promise} operationFn - Function that receives the transaction and performs operations
 * @param {string} mode - Transaction mode ('readonly' or 'readwrite')
 * @returns {Promise<any>} - Promise resolving to the result of the operation function
 */
DatabaseService.prototype.executeTransaction = async function(storeNames, operationFn, mode = 'readwrite') {
  try {
    // Get a transaction object
    const transaction = await this.beginTransaction(storeNames, mode);
    
    return new Promise((resolve, reject) => {
      // Set up transaction event handlers
      transaction.oncomplete = () => {
        console.debug(`Transaction completed successfully for stores: [${storeNames.join(', ')}]`);
        // Resolve with the operation result (stored in resultPromise)
        resultPromise.then(resolve).catch(reject);
      };
      
      transaction.onerror = (event) => {
        console.error(`Transaction error for stores: [${storeNames.join(', ')}]:`, event.target.error);
        reject(event.target.error);
      };
      
      transaction.onabort = (event) => {
        console.warn(`Transaction aborted for stores: [${storeNames.join(', ')}]:`, event.target.error);
        reject(new Error('Transaction was aborted'));
      };
      
      // Execute the operation function with the transaction
      // This should return a promise with the operation result
      const resultPromise = operationFn(transaction);
      
      // If the operation promise rejects, abort the transaction
      resultPromise.catch(error => {
        console.error(`Operation error, aborting transaction:`, error);
        try {
          transaction.abort();
        } catch (abortError) {
          console.error('Error while aborting transaction:', abortError);
        }
      });
    });
  } catch (error) {
    console.error('Failed to execute transaction:', error);
    throw error;
  }
};

/**
 * Get multiple object stores from a transaction.
 * @param {IDBTransaction} transaction - The transaction object
 * @param {string[]} storeNames - Array of store names to retrieve
 * @returns {Object} - Object with store names as keys and IDBObjectStore instances as values
 */
DatabaseService.prototype.getStoresFromTransaction = function(transaction, storeNames) {
  const stores = {};
  storeNames.forEach(storeName => {
    stores[storeName] = transaction.objectStore(storeName);
  });
  return stores;
};

/**
 * Retry a database operation with exponential backoff.
 * @param {function(): Promise<any>} operation - Function that returns a promise for the operation
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise<any>} - Promise resolving to the operation result
 */
DatabaseService.prototype.retryOperation = function(operation, maxRetries = 3, baseDelay = 100) {
  let attempt = 0;
  
  const executeWithRetry = () => {
    attempt++;
    return operation().catch(error => {
      if (attempt <= maxRetries) {
        // Calculate delay with exponential backoff (100ms, 200ms, 400ms...)
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.warn(`Operation failed, retrying (${attempt}/${maxRetries}) after ${delay}ms:`, error);
        
        // Return a new promise that resolves after the delay
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(executeWithRetry());
          }, delay);
        });
      }
      
      // If we've reached max retries, reject with the error
      console.error(`Operation failed after ${maxRetries} retries:`, error);
      throw error;
    });
  };
  
  return executeWithRetry();
};

export { DatabaseService, FULL_ALERT_SYSTEM_SCHEMA };
